/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import { arrayUnion, collection, doc, getDoc, getDocs, query, where, updateDoc } from "firebase/firestore";
import { db, auth } from "../../../firebaseConfig";
// import uniqBy from "lodash/uniqBy";
import { sendCollaboratorInviteEmail } from "../../services/emailService";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Paper from "@mui/material/Paper";
import InputBase from "@mui/material/InputBase";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import IconButton from "@mui/material/IconButton";
import Avatar from "@mui/material/Avatar";
import Person from "@mui/icons-material/Person";
import Snackbar from "@mui/material/Snackbar";
import DeleteIcon from "@mui/icons-material/Delete";
import { Typography } from "@mui/material";
import { forEach } from "lodash";

import "./ShareView.css";

export default function ShareView({ viewId }) {
  const [open, setOpen] = React.useState(false);
  const [collaborators, setCollaborators] = useState([]);
  const [creator, setCreator] = useState(null);
  const [inviteEmail, setInviteEmail] = useState(null);
  // const [inviteEmailError, setInviteEmailError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState(false);
  const [viewName, setViewName] = useState("Untitled View");
  const [currentUser, setCurrentUser] = useState(null);

  // Get current user info on mount
  useEffect(() => {
    const fetchCurrentUser = async () => {
      const user = auth.currentUser;
      if (user) {
        const userData = await fetchUserDataById(user.uid);
        if (userData) {
          setCurrentUser({
            ...userData,
            uid: user.uid,
          });
        }
      }
    };
    fetchCurrentUser();
  }, []);

  const handleClickOpen = () => {
    fetchViewData();
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const isValidEmail = (email) => {
    if (!email || email === "") return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const fetchUserRefByEmail = async (email) => {
    const usersRef = collection(db, "users");
    const q = query(usersRef, where("email", "==", email));
    try {
      const docSnap = await getDocs(q);

      if (!docSnap.empty) {
        return docSnap.docs[0].ref;
      } else {
        console.warn("No such user exists!");
        return false;
      }
    } catch (error) {
      console.error("Error fetching invite user data:", error);
    }
  };

  const handleInviteSubmit = async (event) => {
    event.preventDefault();

    if (!isValidEmail(inviteEmail)) {
      setSnackbarMessage("Please enter a valid email address.");
      setSnackbarOpen(true);
      return;
    }

    // Get inviter info for email
    const inviterName = currentUser
      ? `${currentUser.firstName || ""} ${currentUser.lastName || ""}`.trim() || "A Data Sculpture user"
      : "A Data Sculpture user";
    const inviterEmail = currentUser?.email || "";

    // Ensure we have the organization ID
    let currentOrgId = currentUser?.organizationId;
    if (!currentOrgId && auth.currentUser) {
      const userData = await fetchUserDataById(auth.currentUser.uid);
      currentOrgId = userData?.organizationId;
    }

    if (!currentOrgId) {
      setSnackbarMessage("Error: You must belong to an organization to invite collaborators.");
      setSnackbarOpen(true);
      return;
    }

    // Fetch user by email
    const inviteUserRef = await fetchUserRefByEmail(inviteEmail);

    // If found, add to collaborators
    if (inviteUserRef) {
      // Update collaborators field of the view in Firestore
      const docRef = doc(db, "views", viewId);
      try {
        await updateDoc(docRef, { collaborators: arrayUnion(inviteUserRef) });

        // Add user to collaborators user data array
        const collaboratorUserData = await fetchUserDataById(inviteUserRef.id);
        const updatedCollaborators = collaborators;
        updatedCollaborators.push(collaboratorUserData);
        setCollaborators(updatedCollaborators);

        // Send collaborator invite email (non-blocking)
        sendCollaboratorInviteEmail({
          recipientEmail: inviteEmail,
          recipientName: collaboratorUserData?.firstName || "",
          inviterName,
          inviterEmail,
          viewId,
          viewName,
          isNewUser: false,
          orgId: currentOrgId,
        }).then((result) => {
          if (!result.success) {
            console.warn("Invite email failed to send:", result.error);
          }
        });

        setSnackbarMessage(`${inviteEmail} added as a collaborator!`);
        setSnackbarOpen(true);
        setInviteEmail("");
      } catch (error) {
        console.error("Error updating collaborators in Firestore:", error);
      }
    } else {
      console.warn("Invite user not found:", inviteEmail);

      // Send invite email for non-existing user to sign up
      sendCollaboratorInviteEmail({
        recipientEmail: inviteEmail,
        recipientName: "",
        inviterName,
        inviterEmail,
        viewId,
        viewName,
        isNewUser: true,
        orgId: currentOrgId,
      }).then((result) => {
        if (result.success) {
          setSnackbarMessage(`Invitation sent to ${inviteEmail}. They'll need to create an account to collaborate.`);
        } else {
          console.warn("Invite email failed to send:", result.error);
          setSnackbarMessage(`${inviteEmail} does not have an account. Failed to send invitation email.`);
        }
        setSnackbarOpen(true);
      });
    }
  };

  const fetchUserDataById = async (id) => {
    const docRef = doc(db, "users", id);
    try {
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return docSnap.data();
      } else {
        console.warn("No such user exists!");
        return false;
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
      return false;
    }
  };

  const fetchViewData = async () => {
    const docRef = doc(db, "views", viewId);
    try {
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const viewData = docSnap.data();

        // Set view name for email
        if (viewData.name || viewData.title) {
          setViewName(viewData.name || viewData.title);
        }

        const collaboratorsData = [];
        forEach(viewData.collaborators, async (collaborator) => {
          const collaboratorUser = await fetchUserDataById(collaborator.id);
          collaboratorsData.push(collaboratorUser);
        });
        setCollaborators(collaboratorsData);

        const creatorUser = await fetchUserDataById(viewData.createdBy);
        if (creatorUser) setCreator(creatorUser);
      } else {
        console.error("No such view exists!");
      }
    } catch (error) {
      console.error("Error fetching view data:", error);
    }
  };

  const AccessList = () => {
    let list = creator ? creator.firstName + " " + creator.lastName : "";
    let totalOtherCollaborators = 0;
    if (collaborators.length > 0) {
      collaborators.map((collaborator, i) => {
        if (i < 1) {
          list = `${list}, ${collaborator.firstName + " " + collaborator.lastName}`;
        } else {
          totalOtherCollaborators++;
        }
      });
      if (totalOtherCollaborators > 0) list = `${list} and ${totalOtherCollaborators} other${totalOtherCollaborators > 1 ? "s" : ""}`;
    }
    return <span>{list}</span>;
  };

  const handleRemoveCollaborator = async (collaboratorId) => {
    const removedCollaborator = collaborators.find((collaborator) => collaborator.uid === collaboratorId);

    // Remove collaborator from collaborators data array
    const updatedCollaboratorsRefs = [];
    const updatedCollaborators = [];
    forEach(collaborators, (collaborator) => {
      if (collaborator.uid !== collaboratorId) {
        updatedCollaboratorsRefs.push(doc(db, "users", collaborator.uid));
        updatedCollaborators.push(collaborator);
      }
    });
    setCollaborators(updatedCollaborators);

    // Update collaborators in Firestore
    const docRef = doc(db, "views", viewId);
    try {
      await updateDoc(docRef, { collaborators: updatedCollaboratorsRefs });

      setSnackbarMessage(`${removedCollaborator.email} removed as a collaborator!`);
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error updating collaborators in Firestore:", error);
      setSnackbarMessage(`Error removing ${removedCollaborator.email} as a collaborator!`);
      setSnackbarOpen(true);
    }
  };

  return (
    <React.Fragment>
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose} message={snackbarMessage} />
      <Button
        width="32px"
        id="nav-share-button"
        sx={{ mt: "16px", mr: "8px", fontSize: "0.75rem" }}
        size="small"
        variant="outlined"
        onClick={handleClickOpen}
        className="share-view-button"
      >
        <svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M27.0025 1.68403C21.8755 3.39303 7.74444 8.10302 2.03044 10.008C1.24744 10.269 0.704448 10.984 0.665448 11.809C0.625448 12.633 1.09645 13.398 1.85045 13.733L12.6734 18.543L17.4834 29.365C17.8184 30.12 18.5825 30.591 19.4075 30.551C20.2315 30.511 20.9474 29.969 21.2084 29.186C23.1124 23.471 27.8235 9.34103 29.5325 4.21403C29.7715 3.49503 29.5844 2.70303 29.0484 2.16703C28.5134 1.63103 27.7205 1.44403 27.0025 1.68403ZM26.9814 5.54102L14.5904 17.932L19.3104 28.553L26.9814 5.54102ZM2.66243 11.905L13.1344 16.559L25.3515 4.34202L2.66243 11.905Z"
            fill="var(--Grey7)"
          />
        </svg>
      </Button>
      <Dialog open={open} onClose={handleClose} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
        <DialogTitle sx={{ backgroundColor: "var(--Grey2)", color: "var(--Grey7)" }} id="alert-dialog-title">
          {"Share this view"}
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: "var(--Grey2)", color: "var(--Grey7)" }}>
          <DialogContentText sx={{ backgroundColor: "var(--Grey2)", color: "var(--Grey7)" }} id="alert-dialog-description">
            Add a collaborator for this view.
          </DialogContentText>
          <Paper component="form" sx={{ mt: "4px", p: "2px 4px", display: "flex", alignItems: "center", width: 400 }} onSubmit={handleInviteSubmit}>
            <InputBase
              type="email"
              sx={{ ml: 1, flex: 1,  }}
              placeholder="Invite others by email"
              inputProps={{ "aria-label": "invite others by email" }}
              onChange={(event) => setInviteEmail(event.target.value)}
              value={inviteEmail}
              required
            />
            <Button class="menu-button" type="submit" sx={{ p: "10px" , color: "var(--Grey7)" }}>
              Invite
            </Button>
          </Paper>
          <DialogContentText sx={{ mt: "24px", backgroundColor: "var(--Grey2)", color: "var(--Grey7)" }} id="alert-dialog-description">
            Who has access
          </DialogContentText>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2">
                <AccessList />
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <List dense={true}>
                {creator ? (
                  <ListItem key={creator.uid}>
                    <ListItemAvatar>
                      <Avatar>
                        <Person />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={creator.firstName + " " + creator.lastName} secondary={"Owner"} />
                  </ListItem>
                ) : null}
                {collaborators.map((collaborator) => (
                  <ListItem
                    key={collaborator.uid}
                    secondaryAction={
                      <IconButton edge="end" aria-label="delete" onClick={() => handleRemoveCollaborator(collaborator.uid)}>
                        <DeleteIcon />
                      </IconButton>
                    }
                  >
                    <ListItemAvatar>
                      <Avatar>
                        <Person />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={collaborator.firstName + " " + collaborator.lastName} secondary={"Can edit"} />
                  </ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: "var(--Grey2)", color: "var(--Grey7)" }}>
          <Button class="menu-button" onClick={handleClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}
